import {
  ConditionFieldEnum,
  isForeignKeyOnLinkedResource,
  type BusinessSegmentConditions,
  type RelatedResource,
  type SegmentConditionGroups,
} from '@reach/shared-types/conditions';
import { partnerApi, type ApiResponse } from '../../../lib/api';
import { API_ENDPOINTS } from '../../../lib/constants';
import type {
  AvailableFieldsResponse,
  FieldInfo,
  RelatedSchema,
  ReferencingProperty,
  SegmentSchemaCatalog,
  SchemaDefinitionWithFields,
  SchemaRelationship,
  SchemaRelationshipGraph,
  SchemaSummaryWithFields,
} from './types';
export interface CreateSegmentDefinitionResponse {
  id: string;
  canonicalVersionId: string;
}

export interface CreateSegmentDefinitionParams {
  name: string;
  description: string;
  contents?: BusinessSegmentConditions;
  conditionGroups?: SegmentConditionGroups;
  targetedTenantsFilter?: string[] | null;
}

export interface GetSegmentDefinitionResponse {
  id: string;
  name: string;
  description: string | null;
  canonicalVersionId: string;
  contents: BusinessSegmentConditions;
  conditionGroups?: SegmentConditionGroups;
  updatedAt: string | null;
  targetedTenantsFilter: string[] | null;
  /** Business IDs that have this segment in an active automation; partner cannot deselect these tenants until automations are turned off. */
  businessIdsWithSegmentInActiveAutomation: string[];
  /** For each business ID, the names of automations that use this segment (for conflict messaging). */
  conflictingAutomationNamesByBusinessId: Record<string, string[]>;
}

/**
 * Legacy format = conditions (contents) exist AND condition_groups is empty or not provided.
 * Use this everywhere we need to decide between legacy (conditions) vs new (conditionGroups).
 */
export function isLegacySegmentFormat(args: {
  conditions?: BusinessSegmentConditions | null;
  conditionGroups?: SegmentConditionGroups | null;
}): boolean {
  return (
    args.conditions !== undefined &&
    args.conditions !== null &&
    (!args.conditionGroups || args.conditionGroups.length === 0)
  );
}

export interface UpdateSegmentDefinitionParams {
  name?: string;
  description?: string;
  contents?: BusinessSegmentConditions;
  conditionGroups?: SegmentConditionGroups | null;
  targetedTenantsFilter?: string[] | null;
}

export interface UpdateSegmentDefinitionResponse {
  id: string;
}

/**
 * Create a new segment definition.
 * platformId is automatically derived from auth context.
 */
export async function createSegmentDefinitionFromTemplate(
  params: CreateSegmentDefinitionParams,
): Promise<CreateSegmentDefinitionResponse> {
  const body: Record<string, unknown> = {
    name: params.name,
    description: params.description,
    contents: params.contents ?? [],
    conditionGroups: params.conditionGroups,
  };
  if (params.targetedTenantsFilter !== undefined) {
    body.targetedTenantsFilter = params.targetedTenantsFilter;
  }

  const response = await partnerApi.request<ApiResponse<CreateSegmentDefinitionResponse>>(
    API_ENDPOINTS.PARTNER.SEGMENT_DEFINITIONS,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );

  if (!response.success) {
    throw new Error(response.message || 'Failed to create segment');
  }

  return response.data;
}

/** Get a segment definition by canonical version ID. */
export async function getSegmentDefinition(
  canonicalVersionId: string,
): Promise<GetSegmentDefinitionResponse> {
  const response = await partnerApi.request<ApiResponse<GetSegmentDefinitionResponse>>(
    `${API_ENDPOINTS.PARTNER.SEGMENT_DEFINITIONS}/${canonicalVersionId}`,
  );

  return response.data;
}

/** Update a segment definition. */
export async function updateSegmentDefinition(
  canonicalVersionId: string,
  params: UpdateSegmentDefinitionParams,
): Promise<UpdateSegmentDefinitionResponse> {
  const response = await partnerApi.request<ApiResponse<UpdateSegmentDefinitionResponse>>(
    `${API_ENDPOINTS.PARTNER.SEGMENT_DEFINITIONS}/${canonicalVersionId}`,
    {
      method: 'PUT',
      body: JSON.stringify(params),
    },
  );

  if (!response.success) {
    throw new Error(response.message || 'Failed to update segment');
  }

  return response.data;
}

/**
 * Default built-in fields that are always available for segment conditions
 * Derived from ConditionFieldEnum to stay in sync with backend
 */
const DEFAULT_FIELDS = ConditionFieldEnum.options;

/**
 * Find the foreign key path (property name) that references the contact schema.
 * Checks for $ref matching by ID, plural name, or singular name since $ref uses
 * the schema's plural_name (e.g., "reach:schemas/Customers").
 *
 * Legacy contents-format helper; retains the first path because its response shape supports one entry per target.
 */
function findForeignKeyPathToContact(
  schemaDef: Record<string, unknown> | undefined,
  contactSchemaId: string,
  contactPluralName: string | null,
  contactName: string | null,
): string | null {
  if (!schemaDef || typeof schemaDef !== 'object') return null;
  const properties = schemaDef.properties as Record<string, unknown> | undefined;
  if (!properties || typeof properties !== 'object') return null;

  // Build possible $ref patterns to match
  const possibleRefs = new Set<string>();
  possibleRefs.add(`reach:schemas/${contactSchemaId}`);
  if (contactPluralName) possibleRefs.add(`reach:schemas/${contactPluralName}`);
  if (contactName) possibleRefs.add(`reach:schemas/${contactName}`);

  for (const [key, prop] of Object.entries(properties)) {
    const p = prop as Record<string, unknown> | undefined;
    if (p && typeof p === 'object' && typeof p.$ref === 'string') {
      if (possibleRefs.has(p.$ref)) return key;
    }
  }
  return null;
}

function findPrimaryContactSchema(
  schemas: SchemaDefinitionWithFields[],
  primaryContactSchemaId: string | undefined,
): SchemaDefinitionWithFields | undefined {
  return schemas.find(schema => schema.id === primaryContactSchemaId);
}

/** Scan a schema definition for every property carrying a $ref (single) or items.$ref (array). */
function collectSchemaReferencingProperties(schema: SchemaDefinitionWithFields): ReferencingProperty[] {
  const referencingProperties: ReferencingProperty[] = [];
  const properties = schema.schema?.properties;

  if (properties && typeof properties === 'object') {
    for (const [foreignKeyPath, property] of Object.entries(properties)) {
      if (property && typeof property === 'object') {
        const schemaProperty = property as Record<string, unknown>;
        if (typeof schemaProperty.$ref === 'string') {
          referencingProperties.push({ foreignKeyPath, ref: schemaProperty.$ref, cardinality: 'one' });
        } else {
          const items = schemaProperty.items as Record<string, unknown> | undefined;
          if (items && typeof items === 'object' && typeof items.$ref === 'string') {
            referencingProperties.push({ foreignKeyPath, ref: items.$ref, cardinality: 'many' });
          }
        }
      }
    }
  }
  return referencingProperties;
}

/**
 * Resolve a `reach:schemas/<X>` ref to a schema, case-insensitively, preferring
 * id, then plural_name, then name. Live data mixes all three forms with
 * inconsistent casing (`reach:schemas/listing` for a schema named `Listing`).
 */
export function resolveSchemaRef(
  ref: string,
  schemas: SchemaDefinitionWithFields[],
): SchemaDefinitionWithFields | undefined {
  const refTarget = ref.split('/').pop()?.trim().toLowerCase();
  if (!refTarget) return undefined;
  const byId = schemas.find(s => s.id.toLowerCase() === refTarget);
  if (byId) return byId;
  const byPluralName = schemas.find(
    s => (s.plural_name ?? s.pluralName ?? '').toLowerCase() === refTarget,
  );
  if (byPluralName) return byPluralName;
  return schemas.find(s => (s.name ?? '').toLowerCase() === refTarget);
}

/** Build the relationship graph from schema $ref declarations. */
export function collectSchemaRelationships(schemas: SchemaDefinitionWithFields[]): SchemaRelationship[] {
  const relationships: SchemaRelationship[] = [];

  for (const sourceSchema of schemas) {
    for (const referencingProperty of collectSchemaReferencingProperties(sourceSchema)) {
      const targetSchema = resolveSchemaRef(referencingProperty.ref, schemas);
      if (targetSchema) {
        relationships.push({
          id: `${sourceSchema.id}:${referencingProperty.foreignKeyPath}`,
          sourceSchemaId: sourceSchema.id,
          targetSchemaId: targetSchema.id,
          foreignKeyPath: referencingProperty.foreignKeyPath,
          cardinality: referencingProperty.cardinality,
          label: referencingProperty.foreignKeyPath,
        });
      }
    }
  }

  return relationships;
}

/**
 * Relationships usable from a given subject.
 *
 * - At the contact level (subjectSchemaId === contactSchemaId): relationships
 *   whose foreign key is either on the contact or on the linked record.
 * - Inside a related-resource block (depth ≥ 2): relationships touching the
 *   subject schema, EXCLUDING any that touch the contacts schema — the project
 *   explicitly rules out contact→contact traversal through a shared object.
 */
export function getRelationshipOptionsForSubject(args: {
  graph: SchemaRelationshipGraph;
  subjectSchemaId: string;
}): SchemaRelationship[] {
  const { graph, subjectSchemaId } = args;
  const subjectIsContact = subjectSchemaId === graph.contactSchemaId;

  if (subjectIsContact) {
    return graph.relationships.filter(
      relationship =>
        relationship.targetSchemaId === subjectSchemaId ||
        relationship.sourceSchemaId === subjectSchemaId,
    );
  }

  return graph.relationships.filter(relationship => {
    const touchesSubject =
      relationship.sourceSchemaId === subjectSchemaId || relationship.targetSchemaId === subjectSchemaId;
    const touchesContact =
      relationship.sourceSchemaId === graph.contactSchemaId ||
      relationship.targetSchemaId === graph.contactSchemaId;
    return touchesSubject && !touchesContact;
  });
}

/**
 * Bind a stored related-resource condition to a relationship by its PERSISTED
 * schemaId + foreignKeyPath + foreignKeyOn — never re-derive from discovery, so
 * every existing segment keeps its exact behavior with nothing to backfill.
 * Returns undefined when the stored value no longer appears in the graph; the
 * builder surfaces those read-only instead of silently re-binding.
 */
export function findRelationshipForStoredCondition(args: {
  graph: SchemaRelationshipGraph;
  subjectSchemaId: string;
  storedRelatedResource: Pick<RelatedResource, 'schemaId' | 'foreignKeyPath' | 'foreignKeyOn'>;
}): SchemaRelationship | undefined {
  const { graph, subjectSchemaId, storedRelatedResource } = args;
  if (isForeignKeyOnLinkedResource(storedRelatedResource)) {
    return graph.relationships.find(
      relationship =>
        relationship.sourceSchemaId === storedRelatedResource.schemaId &&
        relationship.targetSchemaId === subjectSchemaId &&
        relationship.foreignKeyPath === storedRelatedResource.foreignKeyPath,
    );
  }
  return graph.relationships.find(
    relationship =>
      relationship.sourceSchemaId === subjectSchemaId &&
      relationship.targetSchemaId === storedRelatedResource.schemaId &&
      relationship.foreignKeyPath === storedRelatedResource.foreignKeyPath,
  );
}

/**
 * Extract field identifier from jsonPath (e.g., ".status" → "status").
 */
function fieldNameFromJsonPath(jsonPath: string): string | null {
  if (!jsonPath || typeof jsonPath !== 'string') return null;
  // Handle ".fieldName" format
  if (jsonPath.startsWith('.')) return jsonPath.slice(1) || null;
  return jsonPath;
}

/** Infer segment builder field type from JSON Schema property. */
function fieldTypeFromSchemaProperty(prop: Record<string, unknown> | undefined): FieldInfo['type'] {
  if (!prop || typeof prop !== 'object') return 'unknown';
  const type = prop.type as string | undefined;
  const format = prop.format as string | undefined;
  if (format === 'date' || format === 'date-time') return 'date';
  if (type === 'string') return 'string';
  if (type === 'number' || type === 'integer') return 'number';
  if (type === 'boolean') return 'boolean';
  if (type === 'array') return 'array';
  if (prop.$ref !== undefined) return 'reference';
  return 'unknown';
}

/** Extract enum values from JSON Schema property; returns strings for dropdown use. */
function enumFromSchemaProperty(prop: Record<string, unknown> | undefined): string[] | undefined {
  if (!prop || typeof prop !== 'object') return undefined;
  const enumVal = prop.enum;
  if (!Array.isArray(enumVal) || enumVal.length === 0) return undefined;
  return enumVal.map(v => (v === null || v === undefined ? '' : String(v)));
}

/**
 * Get property at jsonPath from schema.properties (supports top-level only, e.g. ".status").
 */
function getSchemaProperty(
  schema: SchemaDefinitionWithFields,
  jsonPath: string,
): Record<string, unknown> | undefined {
  const schemaObj = schema.schema;
  if (!schemaObj || typeof schemaObj !== 'object') return undefined;
  const props = schemaObj.properties as Record<string, unknown> | undefined;
  if (!props || typeof props !== 'object') return undefined;
  const fieldName = fieldNameFromJsonPath(jsonPath);
  if (!fieldName) return undefined;
  return props[fieldName] as Record<string, unknown> | undefined;
}

/**
 * Get field names from schema's fields_to_expose (using jsonPath).
 * Returns empty array if fields_to_expose is empty/null (no fallback).
 */
function getSchemaFieldNames(schema: SchemaDefinitionWithFields): string[] {
  const expose = schema.fields_to_expose ?? schema.fieldsToExpose;
  if (!expose || !Array.isArray(expose)) return [];

  const fields: string[] = [];
  for (const f of expose) {
    const fieldName = fieldNameFromJsonPath(f.jsonPath);
    if (fieldName) fields.push(fieldName);
  }
  return fields;
}

/**
 * Get field names from schema's fields_to_expose (explicit name field).
 * For use with legacy format (contents) only. Returns empty array if empty/null.
 */
function getSchemaFieldNamesLegacy(schema: SchemaDefinitionWithFields): string[] {
  const expose = schema.fields_to_expose ?? schema.fieldsToExpose;
  if (!expose || !Array.isArray(expose)) return [];

  const fields: string[] = [];
  for (const f of expose) {
    if (f.name && typeof f.name === 'string') fields.push(f.name);
  }
  return fields;
}

/**
 * Get field metadata (name, type, label, description) from schema's fields_to_expose.
 * - name: jsonPath-derived value persisted to JSON (condition.field); e.g. ".status" → "status".
 * - label: friendly name for dropdown (fields_to_expose.name when present); falls back to name.
 */
function getSchemaFieldInfos(schema: SchemaDefinitionWithFields): FieldInfo[] {
  const expose = schema.fields_to_expose ?? schema.fieldsToExpose;
  if (!expose || !Array.isArray(expose)) return [];

  const infos: FieldInfo[] = [];
  for (const f of expose) {
    const pathName = fieldNameFromJsonPath(f.jsonPath);
    if (!pathName) continue;
    const prop = getSchemaProperty(schema, f.jsonPath);
    const type = fieldTypeFromSchemaProperty(prop);
    const enumValues = enumFromSchemaProperty(prop);
    const label = f.name && typeof f.name === 'string' ? f.name : pathName;
    infos.push({
      name: pathName,
      type,
      label,
      description: typeof f.description === 'string' ? f.description : undefined,
      ...(enumValues && enumValues.length > 0 && { enum: enumValues }),
    });
  }
  return infos.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Fetch available fields from schema definitions.
 * Returns contact fields and related resource schemas that reference the contact schema
 * (for condition groups / cross-object segment conditions).
 */
export async function fetchAvailableFields(): Promise<AvailableFieldsResponse> {
  const [response, schemaMappings] = await Promise.all([
    partnerApi.request<ApiResponse<SchemaDefinitionWithFields[]>>(
      API_ENDPOINTS.PARTNER.SCHEMA_DEFINITIONS,
    ),
    partnerApi.getSchemaMappings(),
  ]);

  const schemas = response.data;
  const contactSchema = findPrimaryContactSchema(
    schemas,
    schemaMappings.contactsSchema?.primarySource?.schemaId,
  );
  const contactSchemaId = contactSchema?.id ?? null;

  const contactFields: string[] = [...DEFAULT_FIELDS];
  const contactFieldsLegacy: string[] = [...DEFAULT_FIELDS];
  if (contactSchema) {
    for (const name of getSchemaFieldNames(contactSchema)) {
      if (!contactFields.includes(name)) contactFields.push(name);
    }
    for (const name of getSchemaFieldNamesLegacy(contactSchema)) {
      if (!contactFieldsLegacy.includes(name)) contactFieldsLegacy.push(name);
    }
  }
  contactFields.sort();
  contactFieldsLegacy.sort();

  const relatedSchemas: RelatedSchema[] = [];
  if (contactSchemaId && contactSchema) {
    const contactPluralName = contactSchema.plural_name ?? contactSchema.pluralName ?? null;
    const contactName = contactSchema.name ?? null;
    const refs = (s: SchemaDefinitionWithFields) => s.reference_schemas ?? s.referenceSchemas ?? [];
    for (const schema of schemas) {
      if (schema.id === contactSchemaId) continue;
      if (!refs(schema)?.includes(contactSchemaId)) continue;
      const schemaObj = schema.schema;
      const foreignKeyPath = findForeignKeyPathToContact(
        schemaObj,
        contactSchemaId,
        contactPluralName,
        contactName,
      );
      if (!foreignKeyPath) continue;
      const fields = getSchemaFieldNames(schema);
      const fieldsLegacy = getSchemaFieldNamesLegacy(schema);
      relatedSchemas.push({
        id: schema.id,
        name: schema.name,
        foreignKeyPath,
        fields: [...fields].sort(),
        fieldsLegacy: fieldsLegacy.length > 0 ? [...fieldsLegacy].sort() : undefined,
      });
    }
  }

  return { contactFields, contactFieldsLegacy, relatedSchemas };
}

/**
 * Fetch available fields with types for the visual segment builder.
 * Returns contact fields plus the full schema relationship graph: every
 * relationship between every pair of schemas, in both directions, with the
 * foreign-key path named — one entry per referencing property.
 */
export async function fetchAvailableFieldsWithTypes(): Promise<SegmentSchemaCatalog> {
  const [response, schemaMappings] = await Promise.all([
    partnerApi.request<ApiResponse<SchemaDefinitionWithFields[]>>(
      API_ENDPOINTS.PARTNER.SCHEMA_DEFINITIONS,
    ),
    partnerApi.getSchemaMappings(),
  ]);

  const schemaDefinitions = response.data;
  const contactSchema = findPrimaryContactSchema(
    schemaDefinitions,
    schemaMappings.contactsSchema?.primarySource?.schemaId,
  );
  const contactSchemaId = contactSchema?.id ?? null;

  const contactFields: FieldInfo[] = DEFAULT_FIELDS.map(name => ({
    name,
    type: 'string',
  }));
  if (contactSchema) {
    const infos = getSchemaFieldInfos(contactSchema);
    for (const info of infos) {
      if (!contactFields.some(f => f.name === info.name)) {
        contactFields.push(info);
      }
    }
  }
  contactFields.sort((a, b) => a.name.localeCompare(b.name));

  const schemas: SchemaSummaryWithFields[] = schemaDefinitions.map(schemaDefinition => ({
    id: schemaDefinition.id,
    name: schemaDefinition.name,
    fields: getSchemaFieldInfos(schemaDefinition),
    externalIdField: schemaDefinition.external_id_field ?? schemaDefinition.externalIdField ?? '',
  }));

  const relationships = collectSchemaRelationships(schemaDefinitions);

  return { contactFields, contactSchemaId, schemas, relationships };
}

export interface PreviewRecipient {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export interface PreviewSegmentRecipientsResponse {
  recipients: PreviewRecipient[];
  pagination: {
    total: number;
    hasNextPage: boolean;
    cursor?: string;
  };
  estimatedTotal: number;
}

/**
 * Preview segment recipients for a given tenant
 * Uses the tenant-scoped endpoint with reach-tenant-id header.
 * Supports both legacy (conditions) and new (conditionGroups) formats.
 */
export async function previewSegmentRecipients(
  tenantId: string,
  conditions?: BusinessSegmentConditions,
  conditionGroups?: SegmentConditionGroups,
  limit = 20,
): Promise<PreviewSegmentRecipientsResponse> {
  const body: Record<string, unknown> = {
    limit,
  };

  if (isLegacySegmentFormat({ conditions, conditionGroups })) {
    body.conditions = conditions;
  } else if (conditionGroups !== undefined && conditionGroups !== null && conditionGroups.length > 0) {
    body.conditionGroups = conditionGroups;
  }

  const response = await partnerApi.request<ApiResponse<PreviewSegmentRecipientsResponse>>(
    API_ENDPOINTS.AUTOMATIONS.PREVIEW_SEGMENT_RECIPIENTS,
    {
      method: 'POST',
      headers: {
        'reach-tenant-id': tenantId,
      },
      body: JSON.stringify(body),
    },
  );

  return response.data;
}
